import React, { useState, useEffect } from 'react';
import { Plus, Edit3, Trash2,
  Phone, Mail, MapPin, Star, Users, 
  ChevronLeft, ChevronRight, X, Save, AlertCircle,
  Check, RefreshCw, Download, Hash, FileText, Globe, 
  User, Calculator, Percent, Handshake, CreditCard, Calendar, Building2,
  FileSpreadsheet, Upload, CheckCircle
} from 'lucide-react';
import NewClient from './NewClient';
import ExcelJS from 'exceljs';

const API_URL = process.env.REACT_APP_API_URL;

export default function ClientsList({ onClientSelected }) {
  // Estados principales
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [activeClient, setActiveClient] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);

  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' });

  // Estados de paginación y filtros
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [filters, setFilters] = useState({
    provincia: '',
    modelo_negocio: ''
  });

  // Estados del formulario de edición
  const [editForm, setEditForm] = useState({});
  const [formErrors, setFormErrors] = useState({});

  // Estados para autocompletado
  const [formasPago, setFormasPago] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    activos: 0,
    nuevos_mes: 0,
    comision_promedio: 0
  });

  // Estado para mostrar/ocultar el formulario de nuevo cliente
  const [showNewClientForm, setShowNewClientForm] = useState(false);

  // ✨ ESTADOS PARA MODAL DE INTEGRACIÓN HISPAFARMA
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [integrationFile, setIntegrationFile] = useState(null);
  const [uploadingIntegration, setUploadingIntegration] = useState(false);
  const [integrationMessage, setIntegrationMessage] = useState('');
  const [integrationError, setIntegrationError] = useState('');
  const [integrationResults, setIntegrationResults] = useState(null);
  const [isIntegrationDragOver, setIsIntegrationDragOver] = useState(false);

  // Cargar formas de pago
  const loadFormasPago = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/formas-pago`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormasPago(data || []);
      }
    } catch (error) {
      console.error('Error al cargar formas de pago:', error);
    }
  };

  // Cargar estadísticas
  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clients-manager/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStats({
            total: data.stats.resumen.total_clientes,
            activos: clients.filter(c => c.activo).length,
            nuevos_mes: data.stats.resumen.nuevos_mes,
            comision_promedio: data.stats.resumen.comision_promedio
          });
        }
      }
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  // Cargar clientes
  const loadClients = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const queryParams = new URLSearchParams({
        limit: '100',
        search: searchTerm,
        provincia: filters.provincia,
        modelo_negocio: filters.modelo_negocio
      });
      
      const response = await fetch(`${API_URL}/api/clients-manager/clients?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setClients(data.clientes || []);
        } else {
          showMessage(data.message || 'Error al cargar clientes', 'danger');
        }
      } else {
        showMessage('Error al conectar con el servidor', 'danger');
      }
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      showMessage('Error al cargar los clientes', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    loadClients();
    loadFormasPago();
    loadStats();
    
    const savedActiveClient = localStorage.getItem('activeClient');
    if (savedActiveClient) {
      try {
        setActiveClient(JSON.parse(savedActiveClient));
      } catch (error) {
        console.error('Error al cargar cliente activo:', error);
      }
    }
  }, []);

  // Recargar clientes cuando cambien los filtros
  useEffect(() => {
    if (searchTerm || filters.provincia || filters.modelo_negocio) {
      const timeoutId = setTimeout(() => {
        loadClients();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm, filters.provincia, filters.modelo_negocio]);

  // ==================== FUNCIONES DE INTEGRACIÓN HISPAFARMA ====================
  
  // Descargar plantilla de Excel
  const handleDownloadTemplate = async () => {
    try {
      setLoading(true);
      showMessage('Generando archivo Excel con todos los clientes...', 'success');
      
      // Obtener TODOS los clientes desde el backend
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clients-manager/clients?limit=10000`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Error al obtener los clientes');
      }
      
      const data = await response.json();
      const todosClientes = data.clientes || [];
      
      if (todosClientes.length === 0) {
        showMessage('No hay clientes para exportar', 'danger');
        return;
      }
      
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Clientes - Integración Hispafarma');

      // Definir columnas con el nuevo orden
      worksheet.columns = [
        { header: 'Razón Social', key: 'razon_social', width: 45 },
        { header: 'ID CentralSalud', key: 'id_centralsalud', width: 20 },
        { header: 'ID Hispafarma (Cirugía)', key: 'id_hispafarma', width: 30 },
        { header: 'ID Medicina Estética', key: 'id_medicinaestetica', width: 30 }
      ];

      // Estilo del encabezado
      worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 };
      worksheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF3CBF9A' }
      };
      worksheet.getRow(1).alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getRow(1).height = 30;

      worksheet.getRow(1).eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      // Agregar datos de todos los clientes
      todosClientes.forEach((cliente, index) => {
        const row = worksheet.addRow({
          razon_social: cliente.razon_social || '',
          id_centralsalud: cliente.id,
          id_hispafarma: cliente.id_cliente_hispafarma || '',
          id_medicinaestetica: cliente.id_clientemedicinaestetica || ''
        });
        
        // Alternar colores de fila
        if (index % 2 === 0) {
          row.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF9F9F9' }
          };
        }

        row.eachCell((cell, colNumber) => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFDDDDDD' } },
            left: { style: 'thin', color: { argb: 'FFDDDDDD' } },
            bottom: { style: 'thin', color: { argb: 'FFDDDDDD' } },
            right: { style: 'thin', color: { argb: 'FFDDDDDD' } }
          };
          cell.alignment = { 
            vertical: 'middle',
            horizontal: colNumber === 1 ? 'left' : 'center' // Razón social alineada a la izquierda
          };
        });
        
        // Resaltar filas que ya tienen IDs configurados
        if (cliente.id_cliente_hispafarma || cliente.id_clientemedicinaestetica) {
          row.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFE8F5E9' } // Verde claro
          };
        }
      });

      // Agregar notas al final
      const lastRow = worksheet.lastRow.number;
      worksheet.addRow([]);
      worksheet.addRow([]);
      
      const notaTituloRow = worksheet.addRow(['📋 INSTRUCCIONES DE USO']);
      notaTituloRow.getCell(1).font = { bold: true, size: 12, color: { argb: 'FF2E7D32' } };
      worksheet.mergeCells(`A${notaTituloRow.number}:D${notaTituloRow.number}`);
      
      worksheet.addRow([]);
      
      const nota1Row = worksheet.addRow(['1️⃣', 'Razón Social e ID CentralSalud son campos de referencia - NO modificar', '', '']);
      nota1Row.getCell(1).font = { bold: true };
      nota1Row.getCell(2).font = { italic: true, color: { argb: 'FF666666' } };
      worksheet.mergeCells(`B${nota1Row.number}:D${nota1Row.number}`);
      
      const nota2Row = worksheet.addRow(['2️⃣', 'ID Hispafarma (Cirugía) es OBLIGATORIO para pedidos de Cirugía Plástica', '', '']);
      nota2Row.getCell(1).font = { bold: true };
      nota2Row.getCell(2).font = { italic: true, color: { argb: 'FFFF6B00' } };
      worksheet.mergeCells(`B${nota2Row.number}:D${nota2Row.number}`);
      
      const nota3Row = worksheet.addRow(['3️⃣', 'ID Medicina Estética es OPCIONAL (solo para pedidos de Medicina Estética)', '', '']);
      nota3Row.getCell(1).font = { bold: true };
      nota3Row.getCell(2).font = { italic: true, color: { argb: 'FF666666' } };
      worksheet.mergeCells(`B${nota3Row.number}:D${nota3Row.number}`);
      
      worksheet.addRow([]);
      
      const nota4Row = worksheet.addRow(['✅', 'Las filas con fondo verde claro ya tienen IDs configurados', '', '']);
      nota4Row.getCell(1).font = { bold: true };
      nota4Row.getCell(2).font = { italic: true, color: { argb: 'FF2E7D32' } };
      worksheet.mergeCells(`B${nota4Row.number}:D${nota4Row.number}`);
      
      const nota5Row = worksheet.addRow(['💾', 'Después de editar, guarde el archivo y súbalo de vuelta para actualizar', '', '']);
      nota5Row.getCell(1).font = { bold: true };
      nota5Row.getCell(2).font = { italic: true, color: { argb: 'FF1976D2' } };
      worksheet.mergeCells(`B${nota5Row.number}:D${nota5Row.number}`);

      // Generar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const fecha = new Date().toISOString().split('T')[0];
      link.setAttribute('href', url);
      link.setAttribute('download', `clientes_hispafarma_${fecha}.xlsx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      showMessage(`✅ Archivo generado con ${todosClientes.length} clientes`, 'success');
      
    } catch (error) {
      console.error('Error al generar Excel:', error);
      showMessage('Error al generar el archivo Excel: ' + error.message, 'danger');
    } finally {
      setLoading(false);
    }
  };
  
  // Manejar selección de archivo
  const handleIntegrationFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validExtensions = ['.xlsx', '.xls'];
      const fileName = file.name.toLowerCase();
      const isValid = validExtensions.some(ext => fileName.endsWith(ext));
      
      if (!isValid) {
        setIntegrationError('Por favor seleccione un archivo Excel (.xlsx o .xls)');
        return;
      }
      
      setIntegrationFile(file);
      setIntegrationError('');
    }
    e.target.value = '';
  };
  
  // Drag and drop
  const handleIntegrationDragOver = (e) => {
    e.preventDefault();
    setIsIntegrationDragOver(true);
  };
  
  const handleIntegrationDragLeave = (e) => {
    e.preventDefault();
    setIsIntegrationDragOver(false);
  };
  
  const handleIntegrationDrop = (e) => {
    e.preventDefault();
    setIsIntegrationDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      const validExtensions = ['.xlsx', '.xls'];
      const fileName = file.name.toLowerCase();
      const isValid = validExtensions.some(ext => fileName.endsWith(ext));
      
      if (!isValid) {
        setIntegrationError('Por favor seleccione un archivo Excel (.xlsx o .xls)');
        return;
      }
      
      setIntegrationFile(file);
      setIntegrationError('');
    }
  };
  
  // Procesar archivo
  const handleUploadIntegration = async () => {
    if (!integrationFile) {
      setIntegrationError('Por favor seleccione un archivo');
      return;
    }
    
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    
    if (!validTypes.includes(integrationFile.type)) {
      setIntegrationError('Por favor seleccione un archivo Excel (.xlsx o .xls)');
      return;
    }
    
    try {
      setUploadingIntegration(true);
      setIntegrationError('');
      setIntegrationMessage('');
      setIntegrationResults(null);
      
      const formData = new FormData();
      formData.append('file', integrationFile);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clientes/bulk-hispafarma-ids`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Error al procesar el archivo');
      }
      
      setIntegrationResults(data);
      setIntegrationMessage(`✅ Integración completada. ${data.updated} clientes actualizados correctamente.`);
      setIntegrationFile(null);
      
    } catch (error) {
      console.error('Error en integración:', error);
      setIntegrationError(error.message || 'Error al procesar el archivo');
    } finally {
      setUploadingIntegration(false);
    }
  };
  
  // Cerrar modal
  const handleCloseIntegrationModal = () => {
    setShowIntegrationModal(false);
    setIntegrationFile(null);
    setIntegrationError('');
    setIntegrationMessage('');
    setIntegrationResults(null);
    setIsIntegrationDragOver(false);
  };
  
  // ==================== FIN FUNCIONES DE INTEGRACIÓN ====================

  // Manejar la selección de cliente
  const handleClientSelect = (client) => {
    if (onClientSelected) {
      onClientSelected(client);
    }
    setActiveClient(client);
    localStorage.setItem('activeClient', JSON.stringify(client));
  };

  // Callback para cuando se crea un nuevo cliente
  const handleClientCreated = (newClient) => {
    setShowNewClientForm(false);
    loadClients();
    showMessage('Cliente creado correctamente', 'success');
    
    if (onClientSelected && newClient) {
      setTimeout(() => {
        onClientSelected(newClient);
      }, 1000);
    }
  };

  // Exportar clientes
  const exportClients = async (format = 'csv') => {
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams({
        format,
        'filters[search]': searchTerm,
        'filters[provincia]': filters.provincia,
        'filters[modelo_negocio]': filters.modelo_negocio
      });
      
      const response = await fetch(`${API_URL}/api/clients-manager/export?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (format === 'csv') {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `clientes_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        showMessage('Archivo exportado correctamente', 'success');
      } else {
        const data = await response.json();
        if (data.success) {
          console.log('Datos exportados:', data.data);
          showMessage(`${data.total} clientes exportados`, 'success');
        }
      }
    } catch (error) {
      console.error('Error al exportar:', error);
      showMessage('Error al exportar clientes', 'danger');
    }
  };

  // Abrir modal de edición
  const openEditModal = (client) => {
    setSelectedClient(client);
    setEditForm({
      id: client.id,
      razon_social: client.razon_social || '',
      alias: client.alias || '',
      cif: client.cif || '',
      calle: client.calle || '',
      localidad: client.localidad || '',
      provincia: client.provincia || '',
      c_postal: client.c_postal || '',
      telefono1: client.telefono1 || '',
      telefono2: client.telefono2 || '',
      mail: client.mail || '',
      mail_admin: client.mail_admin || '',
      mail_admin_2: client.mail_admin_2 || '',
      fax: client.fax || '',
      contacto: client.contacto || '',
      cuenta_contable: client.cuenta_contable || '',
      forma_pago: client.forma_pago || '',
      dia_pago: client.dia_pago || '',
      comision: client.comision || 0,
      modelo_negocio: client.modelo_negocio || '',
      modneg_detalle: client.modneg_detalle || '',
      ahorro: client.ahorro || 0,
      iban: client.iban || '',
      email_calendario: client.email_calendario || '',
      tipo_calendario: client.tipo_calendario || 'ninguno'
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  // Guardar cambios
  const saveClient = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/clients-manager/client/${editForm.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        showMessage('Cliente actualizado correctamente', 'success');
        setShowEditModal(false);
        loadClients();
      } else {
        throw new Error(data.message || 'Error al actualizar cliente');
      }

    } catch (error) {
      console.error('Error al actualizar cliente:', error);
      showMessage(error.message || 'Error al actualizar el cliente', 'danger');
    } finally {
      setSaving(false);
    }
  };

  // Validar formulario
  const validateForm = () => {
    const errors = {};
    
    if (!editForm.razon_social?.trim()) errors.razon_social = 'Requerido';
    if (!editForm.cif?.trim()) errors.cif = 'Requerido';
    if (!editForm.calle?.trim()) errors.calle = 'Requerido';
    if (!editForm.localidad?.trim()) errors.localidad = 'Requerido';
    if (!editForm.c_postal?.toString().trim()) errors.c_postal = 'Requerido';
    
    if (editForm.mail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.mail)) {
      errors.mail = 'Email no válido';
    }

    if (editForm.tipo_calendario !== 'ninguno') {
      if (!editForm.email_calendario || !editForm.email_calendario.trim()) {
        errors.email_calendario = 'El email de calendario es requerido';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email_calendario)) {
        errors.email_calendario = 'Email de calendario no válido';
      } else {
        const domain = editForm.email_calendario.split('@')[1]?.toLowerCase();
        if (editForm.tipo_calendario === 'google' && !domain?.includes('gmail.com') && !domain?.includes('googlemail.com')) {
          errors.email_calendario = 'Para Google Calendar, usa un email @gmail.com';
        } else if (editForm.tipo_calendario === 'outlook' && !domain?.includes('outlook.com') && !domain?.includes('hotmail.com') && !domain?.includes('live.com')) {
          errors.email_calendario = 'Para Outlook Calendar, usa @outlook.com, @hotmail.com o @live.com';
        } else if (editForm.tipo_calendario === 'icloud' && !domain?.includes('icloud.com') && !domain?.includes('me.com')) {
          errors.email_calendario = 'Para iCloud Calendar, usa @icloud.com o @me.com';
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Confirmar eliminación
  const confirmDelete = (client) => {
    setClientToDelete(client);
    setShowDeleteModal(true);
  };

  // Eliminar cliente
  const deleteClient = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clients-manager/client/${clientToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        showMessage('Cliente eliminado correctamente', 'success');
        setShowDeleteModal(false);
        loadClients();
      } else {
        throw new Error(data.message || 'Error al eliminar cliente');
      }

    } catch (error) {
      console.error('Error al eliminar cliente:', error);
      showMessage(error.message || 'Error al eliminar el cliente', 'danger');
    }
  };

  // Utilidades
  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 8080);
  };

  const handleInputChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }

    if (field === 'tipo_calendario' && value !== 'ninguno') {
      if (!editForm.email_calendario) {
        setFormErrors(prev => ({ 
          ...prev, 
          email_calendario: 'Debes ingresar un email de calendario' 
        }));
      }
    } else if (field === 'tipo_calendario' && value === 'ninguno') {
      setEditForm(prev => ({ ...prev, email_calendario: '' }));
      setFormErrors(prev => ({ ...prev, email_calendario: '' }));
    }
  };

  // Datos para selectores
  const getProvincias = () => [
    "Álava", "Albacete", "Alicante", "Almería", "Asturias", "Ávila", "Badajoz", 
    "Barcelona", "Burgos", "Cáceres", "Cádiz", "Cantabria", "Castellón", 
    "Ciudad Real", "Córdoba", "Cuenca", "Gerona", "Granada", "Guadalajara", 
    "Guipúzcoa", "Huelva", "Huesca", "Islas Baleares", "Jaén", "La Coruña", 
    "La Rioja", "Las Palmas", "León", "Lérida", "Lugo", "Madrid", "Málaga", 
    "Murcia", "Navarra", "Orense", "Palencia", "Pontevedra", "Salamanca", 
    "Santa Cruz de Tenerife", "Segovia", "Sevilla", "Soria", "Tarragona", 
    "Teruel", "Toledo", "Valencia", "Valladolid", "Vizcaya", "Zamora", "Zaragoza"
  ];

  const getModelosNegocio = () => [
    { value: 'FIJO_180', label: 'FIJO 180€ + IVA' },
    { value: 'FIJO_40', label: 'FIJO 40€ + IVA' },
    { value: 'FIJO_100', label: 'FIJO 100€ + IVA' },
    { value: 'FIJO_300', label: 'FIJO 300€ + IVA' },
    { value: 'COMISION_TRANSACCIONES', label: 'COMISION TRANSACCIONES' },
    { value: 'COMISION_AHORRO', label: 'COMISION AHORRO' },
    { value: 'ESCALADO', label: 'ESCALADO' }
  ];

  const getTiposCalendario = () => [
    { value: 'ninguno', label: 'Sin sincronización' },
    { value: 'google', label: 'Google Calendar' },
    { value: 'outlook', label: 'Outlook Calendar' },
    { value: 'icloud', label: 'iCloud Calendar' }
  ];

  // Paginación
  const totalPages = Math.ceil(clients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedClients = clients.slice(startIndex, startIndex + itemsPerPage);

  // Si se está mostrando el formulario de nuevo cliente
  if (showNewClientForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center">
          <button
            onClick={() => setShowNewClientForm(false)}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 mr-2" />
            Volver a la lista de clientes
          </button>
        </div>
        
        <NewClient onClientCreated={handleClientCreated} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg" style={{backgroundColor: '#3cbf9a'}}>
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gestión de Clientes</h1>
                <p className="text-gray-600">Administra y selecciona tus clientes</p>
              </div>
            </div>
            
            {/* ✅ BOTONES DEL HEADER - INCLUYE INTEGRACIÓN HISPAFARMA */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => exportClients('csv')}
                className="px-4 py-2 bg-white border-2 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-all flex items-center"
                style={{borderColor: '#10b981'}}
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </button>
              
              <button
                onClick={() => setShowNewClientForm(true)}
                className="px-4 py-2 border-2 rounded-lg font-medium text-white transition-all flex items-center"
                style={{backgroundColor: '#3cbf9a', borderColor: '#3cbf9a'}}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Cliente
              </button>
              
              {/* ✨ BOTÓN DE INTEGRACIÓN HISPAFARMA */}
              <button
                onClick={() => setShowIntegrationModal(true)}
                className="px-4 py-2 rounded-lg font-medium text-white transition-all flex items-center hover:shadow-lg"
                style={{backgroundColor: '#3cbf9a'}}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#2da080'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#3cbf9a'}
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Integración Hispafarma
              </button>
              
              <button
                onClick={loadClients}
                className="px-4 py-2 rounded-lg font-medium text-white transition-all flex items-center"
                style={{backgroundColor: '#3cbf9a'}}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mensaje de estado */}
      {message.text && (
        <div className={`p-4 rounded-lg flex items-center shadow-lg border ${
          message.type === 'success' ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'
        }`}>
          {message.type === 'success' ? (
            <Check className="w-5 h-5 mr-3 text-emerald-600" />
          ) : (
            <AlertCircle className="w-5 h-5 mr-3 text-red-600" />
          )}
          <span className={`font-medium flex-1 ${
            message.type === 'success' ? 'text-emerald-800' : 'text-red-800'
          }`}>
            {message.text}
          </span>
          <button 
            onClick={() => setMessage({ text: '', type: '' })}
            className="ml-auto p-1 hover:bg-white hover:bg-opacity-50 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Filtros y búsqueda */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-1 items-center space-x-4">
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Buscar por nombre, CIF, alias o código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border-2 rounded-lg focus:outline-none border-gray-300"
                style={{borderColor: searchTerm ? '#3cbf9a' : ''}}
                onFocus={(e) => e.target.style.borderColor = '#3cbf9a'}
                onBlur={(e) => !searchTerm && (e.target.style.borderColor = '#e5e7eb')}
              />
            </div>
            
            <select
              value={filters.provincia}
              onChange={(e) => setFilters(prev => ({ ...prev, provincia: e.target.value }))}
              className="px-3 py-2 border-2 rounded-lg focus:outline-none border-gray-300"
              onFocus={(e) => e.target.style.borderColor = '#3cbf9a'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            >
              <option value="">Todas las provincias</option>
              {getProvincias().map(provincia => (
                <option key={provincia} value={provincia}>{provincia}</option>
              ))}
            </select>
            
            <select
              value={filters.modelo_negocio}
              onChange={(e) => setFilters(prev => ({ ...prev, modelo_negocio: e.target.value }))}
              className="px-3 py-2 border-2 rounded-lg focus:outline-none border-gray-300"
              onFocus={(e) => e.target.style.borderColor = '#3cbf9a'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            >
              <option value="">Todos los modelos</option>
              {getModelosNegocio().map(modelo => (
                <option key={modelo.value} value={modelo.value}>{modelo.label}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Mostrar:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
              className="px-2 py-1 border rounded"
            >
              <option value={12}>12</option>
              <option value={24}>24</option>
              <option value={48}>48</option>
            </select>
          </div>
        </div>
      </div>

      {/* Vista de clientes */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="w-8 h-8 border-4 border-gray-300 rounded-full animate-spin" style={{borderTopColor: '#3cbf9a'}}></div>
        </div>
      ) : clients.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay clientes</h3>
          <p className="text-gray-600">
            {searchTerm || filters.provincia || filters.modelo_negocio 
              ? 'No se encontraron clientes con los filtros aplicados' 
              : 'Aún no tienes clientes registrados'
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {paginatedClients.map(client => (
            <div key={client.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all border-2 border-transparent hover:border-gray-200 cursor-pointer"
                 onClick={() => handleClientSelect(client)}>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{backgroundColor: '#3cbf9a'}}>
                      {client.razon_social.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-sm leading-tight">
                        {client.razon_social.length > 20 ? 
                          client.razon_social.substring(0, 20) + '...' : 
                          client.razon_social}
                      </h3>
                      <p className="text-xs text-gray-500">ID: {client.id}</p>
                    </div>
                  </div>
                  {activeClient?.id === client.id && (
                    <Star className="w-5 h-5 text-yellow-500 fill-current" />
                  )}
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-xs text-gray-600">
                    <Hash className="w-3 h-3 mr-2" />
                    {client.cif}
                  </div>
                  {client.telefono1 && (
                    <div className="flex items-center text-xs text-gray-600">
                      <Phone className="w-3 h-3 mr-2" />
                      {client.telefono1}
                    </div>
                  )}
                  {client.mail && (
                    <div className="flex items-center text-xs text-gray-600">
                      <Mail className="w-3 h-3 mr-2" />
                      {client.mail.length > 25 ? client.mail.substring(0, 25) + '...' : client.mail}
                    </div>
                  )}
                  <div className="flex items-center text-xs text-gray-600">
                    <MapPin className="w-3 h-3 mr-2" />
                    {client.localidad}, {client.provincia || 'N/A'}
                  </div>
                </div>
                
                {client.modelo_negocio && (
                  <div className="mb-4">
                    <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {client.modelo_negocio.replace('_', ' ')}
                    </span>
                  </div>
                )}
                
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(client);
                      }}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      title="Editar"
                    >
                      <Edit3 className="w-4 h-4 text-gray-600" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDelete(client);
                      }}
                      className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-xl shadow-sm p-6">
          <div className="text-sm text-gray-600">
            Mostrando {startIndex + 1} - {Math.min(startIndex + itemsPerPage, clients.length)} de {clients.length} clientes
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  currentPage === i + 1 
                    ? 'text-white' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                style={{backgroundColor: currentPage === i + 1 ? '#3cbf9a' : ''}}
              >
                {i + 1}
              </button>
            ))}
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ✨ MODAL DE INTEGRACIÓN HISPAFARMA */}
      {showIntegrationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full">
            {/* Header del Modal */}
            <div className="text-white p-4 border-b" style={{ backgroundColor: '#3cbf9a' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 rounded-lg mr-3" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
                    <FileSpreadsheet size={20} style={{ color: 'white' }} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold" style={{ color: 'white' }}>Integración Masiva - Hispafarma</h2>
                    <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Unificar IDs de CentralSalud con IDs de Hispafarma
                    </p>
                  </div>
                </div>
                <button 
                  onClick={handleCloseIntegrationModal}
                  className="p-2 rounded-lg transition-all duration-200 hover:bg-red-500"
                  style={{ color: 'white' }}
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Contenido del Modal */}
            <div className="p-6">
              {/* Información */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-900 mb-2 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  ¿Qué hace esta integración?
                </h3>
                <p className="text-sm text-blue-800 mb-2">
                  Esta herramienta actualiza masivamente los IDs de Hispafarma de tus clientes:
                </p>
                <ul className="text-sm text-blue-800 space-y-1 ml-4">
                  <li>• <strong>ID Cliente Hispafarma (Cirugía):</strong> Obligatorio - Para pedidos de Cirugía Plástica</li>
                  <li>• <strong>ID Medicina Estética:</strong> Opcional - Para pedidos de Medicina Estética</li>
                </ul>
              </div>

              {/* Descargar Plantilla */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  1. Descargar Plantilla de Excel
                </label>
                <button
                  onClick={handleDownloadTemplate}
                  className="w-full px-4 py-3 text-white rounded-lg transition-colors flex items-center justify-center"
                  style={{ backgroundColor: '#3cbf9a' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#2da080'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#3cbf9a'}
                >
                  <Download size={18} className="mr-2" />
                  Descargar Plantilla Excel
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  La plantilla incluye ejemplos con 3 columnas: ID Cliente CentralSalud (obligatorio), 
                  ID Cliente Hispafarma (obligatorio), ID Medicina Estética (opcional)
                </p>
              </div>

              {/* Subir Archivo */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  2. Subir Archivo Completado
                </label>
                <div
                  className={`border-2 border-dashed rounded-xl p-6 transition-all duration-300 ${
                    isIntegrationDragOver 
                      ? 'border-green-500 bg-green-50' 
                      : integrationFile
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                  }`}
                  onDragOver={handleIntegrationDragOver}
                  onDragLeave={handleIntegrationDragLeave}
                  onDrop={handleIntegrationDrop}
                >
                  <input
                    type="file"
                    id="integration-file-upload"
                    className="hidden"
                    accept=".xlsx,.xls"
                    onChange={handleIntegrationFileSelect}
                    disabled={uploadingIntegration}
                  />
                  <label htmlFor="integration-file-upload" className="cursor-pointer flex flex-col items-center">
                    {integrationFile ? (
                      <>
                        <div className="bg-blue-100 p-3 rounded-full mb-3">
                          <FileSpreadsheet size={32} className="text-blue-600" />
                        </div>
                        <p className="font-semibold text-gray-800">{integrationFile.name}</p>
                        <p className="text-sm text-gray-600 mt-1">
                          {(integrationFile.size / 1024).toFixed(2)} KB
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="bg-gray-100 p-3 rounded-full mb-3">
                          <Upload size={32} className="text-gray-400" />
                        </div>
                        <p className="font-semibold text-gray-800">Arrastre su archivo o haga clic aquí</p>
                        <p className="text-sm text-gray-600">Excel (.xlsx, .xls)</p>
                      </>
                    )}
                  </label>
                </div>
              </div>

              {/* Mensajes de resultado */}
              {integrationError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-red-800">Error</p>
                    <p className="text-sm text-red-700">{integrationError}</p>
                  </div>
                </div>
              )}

              {integrationMessage && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-green-800">¡Éxito!</p>
                    <p className="text-sm text-green-700">{integrationMessage}</p>
                  </div>
                </div>
              )}

              {/* Resultados detallados */}
              {integrationResults && (
                <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">Resultados de la Integración</h4>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-white p-3 rounded-lg border border-gray-200">
                      <p className="text-2xl font-bold text-blue-600">{integrationResults.processed}</p>
                      <p className="text-xs text-gray-600">Procesados</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-gray-200">
                      <p className="text-2xl font-bold text-green-600">{integrationResults.updated}</p>
                      <p className="text-xs text-gray-600">Actualizados</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-gray-200">
                      <p className="text-2xl font-bold text-red-600">{integrationResults.errors}</p>
                      <p className="text-xs text-gray-600">Errores</p>
                    </div>
                  </div>
                  {integrationResults.details && integrationResults.details.length > 0 && (
                    <div className="mt-3 max-h-40 overflow-y-auto">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Detalles:</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {integrationResults.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className={`mr-2 ${detail.success ? 'text-green-600' : 'text-red-600'}`}>
                              {detail.success ? '✓' : '✗'}
                            </span>
                            <span>{detail.message}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Botones de acción */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCloseIntegrationModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cerrar
                </button>
                <button
                  onClick={handleUploadIntegration}
                  disabled={!integrationFile || uploadingIntegration}
                  className="px-6 py-2 text-white rounded-lg transition-all flex items-center"
                  style={{ 
                    backgroundColor: (!integrationFile || uploadingIntegration) ? '#9ca3af' : '#3cbf9a' 
                  }}
                  onMouseEnter={(e) => !(!integrationFile || uploadingIntegration) && (e.target.style.backgroundColor = '#2da080')}
                  onMouseLeave={(e) => !(!integrationFile || uploadingIntegration) && (e.target.style.backgroundColor = '#3cbf9a')}
                >
                  {uploadingIntegration ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Procesar Integración
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE EDICIÓN - SE MANTIENE IGUAL */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          {/* ... (mantener el modal de edición completo tal como está) ... */}
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <AlertCircle className="w-6 h-6 text-red-500 mr-3" />
              <h2 className="text-lg font-bold text-gray-900">Confirmar Eliminación</h2>
            </div>
            
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de que quieres eliminar el cliente <strong>{clientToDelete?.razon_social}</strong>? 
              Esta acción no se puede deshacer.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={deleteClient}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}